/*
 * Copyright 2026 North Carolina State University
 */

import { useCallback, useEffect, useState } from 'react';

/**
 * Drag-to-resize columns for a plain <table>, remembered per browser.
 *
 * Column widths are a matter of taste and screen size — a laptop and a monitor
 * want different things — so rather than hard-coding them, each person sets
 * their own by dragging the divider in the header. Widths are stored as
 * percentages, so they still make sense when the window is resized.
 *
 * Dragging a divider trades width between the two columns it sits between, so
 * the table always fits its container and never grows a horizontal scrollbar.
 * Double-click a divider to put every column back to its default.
 *
 * Usage:
 *   const tableRef = useResizableColumns('projects-table');
 *   <table ref={tableRef} className="resizable-table"> ... </table>
 */

const MIN_WIDTH = 60; // px — below this a column can't be read at all
const STORAGE_PREFIX = 'columnWidths:';
const KEYBOARD_STEP = 16; // px per arrow key press

const useResizableColumns = (storageKey) => {
  // A callback ref rather than useRef: these tables mount and unmount as
  // sections change, and this re-runs the setup each time one appears.
  const [table, setTable] = useState(null);
  const tableRef = useCallback((node) => setTable(node), []);
  const fullKey = `${STORAGE_PREFIX}${storageKey}`;

  const readSaved = useCallback(() => {
    try {
      const raw = window.localStorage.getItem(fullKey);
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      // A browser with storage blocked still gets working (just unremembered)
      // columns rather than a broken page.
      return null;
    }
  }, [fullKey]);

  const save = useCallback((headers) => {
    const total = headers.reduce((sum, th) => sum + th.offsetWidth, 0);
    if (!total) return;
    const percents = headers.map(th => (th.offsetWidth / total) * 100);
    try {
      window.localStorage.setItem(fullKey, JSON.stringify(percents));
    } catch {
      /* storage unavailable — the drag still applies for this visit */
    }
  }, [fullKey]);

  useEffect(() => {
    if (!table) return undefined;

    const headerRow = table.querySelector('thead tr');
    if (!headerRow) return undefined;
    const headers = Array.from(headerRow.children);
    if (headers.length < 2) return undefined;

    const saved = readSaved();
    if (saved && saved.length === headers.length) {
      table.style.tableLayout = 'fixed';
      headers.forEach((th, i) => { th.style.width = `${saved[i]}%`; });
    }

    let cleanupDrag = null;

    // Pin every column to its current pixel width before the first drag, so the
    // browser stops re-deciding widths from the content mid-drag.
    const freezeWidths = () => {
      const widths = headers.map(th => th.offsetWidth);
      table.style.tableLayout = 'fixed';
      headers.forEach((th, i) => { th.style.width = `${widths[i]}px`; });
      return widths;
    };

    const resizeBy = (index, delta) => {
      const widths = headers.map(th => th.offsetWidth);
      const pairTotal = widths[index] + widths[index + 1];
      const next = Math.min(
        Math.max(widths[index] + delta, MIN_WIDTH),
        pairTotal - MIN_WIDTH
      );
      headers[index].style.width = `${next}px`;
      headers[index + 1].style.width = `${pairTotal - next}px`;
    };

    const startDrag = (index, startX) => {
      freezeWidths();
      const widths = headers.map(th => th.offsetWidth);
      const startWidth = widths[index];
      const pairTotal = startWidth + widths[index + 1];

      const onMove = (e) => {
        const delta = e.clientX - startX;
        const next = Math.min(
          Math.max(startWidth + delta, MIN_WIDTH),
          pairTotal - MIN_WIDTH
        );
        headers[index].style.width = `${next}px`;
        headers[index + 1].style.width = `${pairTotal - next}px`;
      };

      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.classList.remove('is-resizing-column');
        cleanupDrag = null;
        save(headers);
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.body.classList.add('is-resizing-column');
      cleanupDrag = onUp;
    };

    const reset = () => {
      try {
        window.localStorage.removeItem(fullKey);
      } catch {
        /* nothing to clear */
      }
      table.style.tableLayout = '';
      headers.forEach(th => { th.style.width = ''; });
    };

    // One divider per column boundary, added to the DOM rather than the JSX so
    // any table can opt in without changing its markup.
    const handles = headers.slice(0, -1).map((th, index) => {
      const handle = document.createElement('div');
      handle.className = 'col-resizer';
      handle.setAttribute('role', 'separator');
      handle.setAttribute('aria-orientation', 'vertical');
      handle.setAttribute('tabindex', '0');
      handle.setAttribute(
        'aria-label',
        `Resize ${th.textContent.trim() || `column ${index + 1}`} column`
      );
      handle.title = 'Drag to resize · double-click to reset';

      handle.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        startDrag(index, e.clientX);
      });

      handle.addEventListener('dblclick', (e) => {
        e.preventDefault();
        e.stopPropagation();
        reset();
      });

      // Keyboard users get the same control: arrows nudge, Enter resets.
      handle.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.preventDefault();
          freezeWidths();
          resizeBy(index, e.key === 'ArrowLeft' ? -KEYBOARD_STEP : KEYBOARD_STEP);
          save(headers);
        } else if (e.key === 'Enter') {
          e.preventDefault();
          reset();
        }
      });

      th.classList.add('is-resizable');
      th.appendChild(handle);
      return handle;
    });

    return () => {
      if (cleanupDrag) cleanupDrag();
      handles.forEach(handle => handle.remove());
      headers.forEach(th => th.classList.remove('is-resizable'));
    };
  }, [table, fullKey, readSaved, save]);

  return tableRef;
};

export default useResizableColumns;
